import { filter } from "@suse-o11y/filter";
import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { Lazy } from "@suse-o11y/lazy";
import { prop } from "@suse-o11y/prop";
import { pullObject } from "@suse-o11y/pull-object";
import type { TemplateVariableValue, TemplateVariables } from "@suse-o11y/template";
import { uniqueBy } from "@suse-o11y/unique-by";

export interface TemplateVariableDefinition {
	name: string;
	value: TemplateVariableValue<null>;
	detail?: string;
}

export type TemplateVariableSuggestion = Pick<TemplateVariableDefinition, "name" | "detail">;

export class TemplateVariableDefinitions {
	private readonly lazyValues = Lazy.create(() => pullObject(this.definitions, prop("name"), prop("value")));

	public constructor(private readonly definitions: ReadonlyArray<TemplateVariableDefinition>) {}

	/**
	 * Get the template variable values as a key-value object to use in template interpolation.
	 *
	 * @return The template variable values.
	 */
	public get values(): TemplateVariables<null> {
		return this.lazyValues.get();
	}

	/**
	 * Get the template variable definitions for display purposes in UI components.
	 *
	 * @return An array of template variable definitions with name and detail.
	 */
	public get suggestions(): Array<TemplateVariableSuggestion> {
		return this.definitions as Array<TemplateVariableDefinition>;
	}

	public isEqual(other: TemplateVariableDefinitions): boolean {
		return isDeepEqual(this.values, other.values);
	}

	public diff(other: TemplateVariableDefinitions): TemplateVariableDefinitions {
		const changed = [
			...filter(this.definitions, ({ name, value }) => other.values[name] !== value),
			...filter(other.definitions, ({ name }) => !(name in this.values)),
		];

		return new TemplateVariableDefinitions(uniqueBy(changed, prop("name")));
	}

	public isEmpty(): boolean {
		return this.definitions.length === 0;
	}
}
